import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle, Flag, Shield } from 'lucide-react';

const API_URL = window.location.origin;

const REPORT_REASONS = [
  { id: 'solicitation', label: 'Unwanted solicitation or selling' },
  { id: 'inappropriate', label: 'Inappropriate behavior' },
  { id: 'unsafe', label: 'Feels unsafe or threatening' },
  { id: 'spam', label: 'Spam or repeated messages' },
  { id: 'harassment', label: 'Harassment or bullying' },
  { id: 'other', label: 'Other (please describe)' }
];

const ReportDialog = ({ 
  isOpen, 
  onClose, 
  contentType, // 'user', 'post', 'comment', 'dm'
  reportedUserId = null,
  reportedContentId = null,
  reportedUserName = ''
}) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }

    if (reason === 'other' && !details.trim()) {
      toast.error('Please provide details');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/report`,
        {
          reported_user_id: reportedUserId,
          reported_content_id: reportedContentId,
          content_type: contentType,
          reason: reason,
          details: details.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Report submitted. Thank you for helping keep our community safe.');
      onClose();
      setReason('');
      setDetails('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const getTitle = () => {
    switch (contentType) {
      case 'user': return `Report ${reportedUserName || 'User'}`;
      case 'post': return 'Report Post';
      case 'comment': return 'Report Comment';
      case 'dm': return 'Report Message';
      default: return 'Report';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-500" />
            {getTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-600 mb-4">
            Help us understand what happened. Your report is confidential.
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-forest mb-3 block">
                Why are you reporting this?
              </Label>
              <RadioGroup value={reason} onValueChange={setReason}>
                {REPORT_REASONS.map((r) => (
                  <div key={r.id} className="flex items-center space-x-3 py-2">
                    <RadioGroupItem value={r.id} id={r.id} />
                    <Label htmlFor={r.id} className="text-sm text-gray-700 cursor-pointer">
                      {r.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="details" className="text-sm font-medium text-forest mb-2 block">
                Additional details {reason === 'other' ? '(required)' : '(optional)'}
              </Label>
              <Textarea
                id="details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Tell us more about what happened..."
                className="input-base min-h-[80px]"
              />
            </div>
          </div>

          <div className="mt-4 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
            <Shield className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700">
              After reporting, this content will be hidden from your view. 
              Our team will review and take appropriate action.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !reason}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Report button component to use anywhere
export const ReportButton = ({ 
  contentType, 
  reportedUserId, 
  reportedContentId, 
  reportedUserName,
  variant = 'ghost',
  size = 'sm',
  showText = false,
  asDropdownItem = false,
  className = ''
}) => {
  const [showDialog, setShowDialog] = useState(false);

  if (asDropdownItem) {
    return (
      <>
        <div
          onClick={() => setShowDialog(true)}
          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground text-red-500 hover:text-red-600"
          data-testid="report-btn"
        >
          <Flag className="h-4 w-4 mr-2" />
          Report
        </div>

        <ReportDialog
          isOpen={showDialog}
          onClose={() => setShowDialog(false)}
          contentType={contentType}
          reportedUserId={reportedUserId}
          reportedContentId={reportedContentId}
          reportedUserName={reportedUserName}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setShowDialog(true)}
        className={`text-gray-400 hover:text-red-500 ${className}`}
        data-testid="report-btn"
      >
        <Flag className="h-4 w-4" />
        {showText && <span className="ml-1">Report</span>}
      </Button>

      <ReportDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        contentType={contentType}
        reportedUserId={reportedUserId}
        reportedContentId={reportedContentId}
        reportedUserName={reportedUserName}
      />
    </>
  );
};

export default ReportDialog;
